/* eslint-disable complexity */
import {trackEvent} from '@hconnect/common/logging/Analytics'
import {useFieldConfigurationContext} from '@hconnect/tuc-processes/configuration/useConfigurationContext'
import {useDateTime} from '@hconnect/tuc-processes/fields/dateTime/useDateTime'
import {Box} from '@mui/material'
import moment from 'moment'
import React, {useCallback, useMemo} from 'react'
import {useTranslation} from 'react-i18next'

import {Banner} from '../../../../../../orderIntake/components/shared/Banner'
import {RmcOrderIntakeStepType} from '../../../../../../orderIntake/config/stepTypes'
import {useStepValidationTrigger} from '../../../../../../orderIntake/validation/useStepValidation'
import {TimezoneBanner} from '../../../../../../orderManagement/components/TimezoneBanner'

import {isSlotBeforeMinimum} from '../timeFloor'

import {TimeSlotPicker} from './TimeSlotPicker'
import {useSlots} from './useSlots'

export interface OrderProcessTimeSlotPickerStepProps {
  availableSlots: string[]
  autoConfirmAvailability: {
    isOutOfLeadTime: boolean
    isQuantityExceeded: boolean
    isUnavailable: boolean
  }
  selectedSlot: {
    isAvailable: boolean
    showUnavailableBanner: boolean
  }
  isLoading: boolean
  showOutOfOperationalHoursSlots: boolean
  /**
   * When set (unconfirmed UE/CC order with a non-zero offset), slots earlier than this rescheduled
   * delivery time are hidden on the same day. Omitted in order intake and normal change orders.
   */
  minimumDateTime?: string
}
/**
 * Component for the Time step
 * Handles delivery time selection for RMC orders
 * Redesigned to match mobile app implementation with slot management
 */
export const OrderProcessTimeSlotPickerStep: React.FC<OrderProcessTimeSlotPickerStepProps> = ({
  autoConfirmAvailability,
  availableSlots,
  isLoading,
  selectedSlot,
  showOutOfOperationalHoursSlots,
  minimumDateTime
}) => {
  const {t} = useTranslation()
  const {dateTime, setDateTime} = useDateTime()
  const {triggerStepValidation} = useStepValidationTrigger(RmcOrderIntakeStepType.TIME)

  const dateTimeConfiguration = useFieldConfigurationContext('dateTime')
  const jobSiteTimezone = dateTimeConfiguration?.businessHours.timeZone
  const selectedDate = dateTime?.[0] ?? null

  const {businessHours} = useDateTime()
  // Extend time slots with instant approval information
  const generatedSlots = useSlots(
    dateTime?.[0],
    availableSlots,
    businessHours?.earliestPossibleLoadTime,
    businessHours?.latestPossibleLoadTime,
    !showOutOfOperationalHoursSlots ? businessHours?.earliestPossibleLoadTime : '00:00',
    !showOutOfOperationalHoursSlots ? businessHours?.latestPossibleLoadTime : '23:59',
    autoConfirmAvailability.isOutOfLeadTime,
    businessHours?.timeZone
  )

  // Floor the list at the rescheduled delivery time for unconfirmed UE/CC orders: hide any slot
  // earlier than `minimumDateTime` on the same day (no-op when `minimumDateTime` is undefined).
  const allSlots = useMemo(
    () => generatedSlots.filter((slot) => !isSlotBeforeMinimum(dateTime?.[0], slot.slot, minimumDateTime)),
    [generatedSlots, dateTime, minimumDateTime]
  )

  const offHoursSlots = useMemo(() => {
    return allSlots.filter((slot) => slot.isOutsideLoadHours).map((slot) => slot.slot)
  }, [allSlots])

  // Handle time selection - do NOT call onSuccess() immediately
  const handleTimeSelection = useCallback(
    async (selectedTime: moment.Moment) => {
      const timeSlot = selectedTime.format('HH:mm')
      const isInstantApprovalSlot = availableSlots.includes(timeSlot)

      // Track time slot selection
      trackEvent('rmcTimeSlotSelected', {
        businessLine: 'RMC',
        selectedTime: timeSlot,
        selectedDate: selectedTime.format('YYYY-MM-DD'),
        isInstantApprovalSlot,
        isOutOfAutoConfirmLeadTime: autoConfirmAvailability.isOutOfLeadTime,
        instantApprovalUnavailable: autoConfirmAvailability.isUnavailable
      })

      setDateTime([selectedTime])

      // Trigger validation after time selection
      await triggerStepValidation()
    },
    [
      setDateTime,
      triggerStepValidation,
      availableSlots,
      autoConfirmAvailability.isOutOfLeadTime,
      autoConfirmAvailability.isUnavailable
    ]
  )

  // Auto approval status configuration
  const autoApprovalStatus = useMemo(() => {
    if (autoConfirmAvailability.isUnavailable) {
      return {
        variant: 'warning' as const,
        message: t('orderIntake.rmc.steps.time.unableToFetchAvailability')
      }
    }

    if (autoConfirmAvailability.isQuantityExceeded) {
      return {
        variant: 'warning' as const,
        message: t('orderIntake.rmc.steps.time.quantityExceedsLimit')
      }
    }

    if (autoConfirmAvailability.isOutOfLeadTime) {
      return {
        variant: 'warning' as const,
        message: t('orderIntake.rmc.steps.time.instantApprovalUnavailableDispatchNeeded')
      }
    }
  }, [
    autoConfirmAvailability.isOutOfLeadTime,
    autoConfirmAvailability.isQuantityExceeded,
    autoConfirmAvailability.isUnavailable,
    t
  ])

  return (
    <Box
      data-test-id="time-step-content"
      sx={{
        backgroundColor: '#FFFFFF',
        borderRadius: '8px',
        display: 'flex',
        flexDirection: 'column',
        gap: '24px',
        minHeight: '400px'
      }}
    >
      {/* Auto approval status banner */}
      {autoApprovalStatus && (
        <Banner
          variant={autoApprovalStatus.variant}
          message={autoApprovalStatus.message}
          marginTop="0px"
          marginBottom="8px"
          testId="time-step-auto-approval-status"
        />
      )}

      {/* Timezone Warning Banner */}
      <TimezoneBanner
        jobSiteTimezone={jobSiteTimezone}
        selectedDate={selectedDate}
        data-test-id="time-picker-timezone-banner"
      />

      {/* Main Content */}
      <Box sx={{flex: 1, display: 'flex', flexDirection: 'column', gap: '16px'}}>
        {/* Dispatch approval warning banner */}
        {selectedSlot.showUnavailableBanner && (
          <Banner
            variant="warning"
            message={t('orderIntake.rmc.steps.time.dispatchApprovalRequired')}
            marginTop="0px"
            testId="time-step-dispatch-approval-warning"
          />
        )}
        {/* Time Selection */}
        <Box sx={{flex: 1}}>
          <TimeSlotPicker
            initialTime={dateTime?.[0]}
            allTimeSlots={allSlots}
            availableSlots={availableSlots}
            offHoursSlots={offHoursSlots}
            onSelectedTimeChange={handleTimeSelection}
            isLoading={isLoading}
          />
        </Box>
      </Box>
    </Box>
  )
}
